import React, { useMemo, useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { isUserInfo, User, userInfoTagList } from '../types/user';
import { isNotUndefined } from '../utils/is_not_undefined';
import { memoNamed } from '../utils/react/memo-named';
import firebase from 'firebase/app';

interface Props {
  docID: string;
  user: User;
  showAdditionalInfo: string;
  setShowAdditionalInfo: (showAdditionalInfo: string) => void;
  editable: string;
  setEditable: (editable: string) => void;
  isAdmin: boolean;
  setShowPayInfo: (showPayInfo: string) => void;
  hasPrivilege: boolean;
}

const Name = styled.div`
  font-weight: bold;
  display: inline-block;
`;

const Registered = styled.div`
  display: inline-block;
  color: red;
`;

const IsAdmin = styled.div`
  display: inline-block;
  color: green;
`;

const TdTag = styled.td`
  width: 25%;
  font-weight: bold;
`;

const TdVal = styled.td`
  width: 75%;
`;

const TableHead = styled.table`
  width: 100%;
`;

const Table = styled.table`
  width: 90%;
  margin: 0 auto;
`;

const Root = styled.div`
  padding: 5px;
  border-radius: 5px;
  border: solid;
  border-width: 1px;
`;

const TdLeft = styled.td`
  text-align: left;
  vertical-align: bottom;
`;

const TdRight = styled.td`
  text-align: right;
  vertical-align: bottom;
`;

const userInfoTagListShow = userInfoTagList.filter(
  (u) =>
    ![
      'sei',
      'sei_kana',
      'mei',
      'mei_kana',
      'enter_year',
      'final_education',
      'workplace',
      'contact',
      'sex',
      'comment',
    ].includes(u.tagKey)
);

export const NamesUser = memoNamed<Props>('NamesUser', (props: Props) => {
  const userProperties = useMemo<
    {
      tagKey: string;
      tagName: string;
      displayValue: string;
    }[]
  >(
    () =>
      userInfoTagListShow
        .map((p) => {
          const property = props.user[p.tagKey];
          if (isUserInfo(property) && !property.open && !props.isAdmin)
            return undefined;
          return {
            tagKey: p.tagName,
            tagName: p.tagName,
            displayValue: isUserInfo(property)
              ? property.value
              : property.toString(),
          };
        })
        .filter(isNotUndefined),
    [props.user, props.isAdmin]
  );

  const selectShowPayInfo = useCallback(() => {
    props.setShowPayInfo(props.docID);
  }, [props]);

  const selectShowAdditionalInfo = useCallback(() => {
    props.setShowAdditionalInfo(props.docID);
  }, [props]);

  const cancelShowAdditionalInfo = useCallback(() => {
    props.setShowAdditionalInfo('');
  }, [props]);

  const selectEditable = useCallback(() => {
    props.setEditable(props.docID);
  }, [props]);

  const [emailInvalid, setEmailInvalid] = useState<boolean>(false);
  const [currentInvalid, setCurrentInvalid] = useState<boolean>(false);
  const [homeInvalid, setHomeInvalid] = useState<boolean>(false);
  const [invalidUpdating, setInvalidUpdating] = useState<boolean>(false);

  useEffect(() => {
    setEmailInvalid(props.user.emailValid === 'invalid');
    setCurrentInvalid(props.user.currentValid === 'invalid');
    setHomeInvalid(props.user.homeValid === 'invalid');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEmailInvalidChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const _checked = event.target.checked;
      setEmailInvalid(_checked);
      setInvalidUpdating(true);
      const db = firebase.firestore();
      db.collection('Users')
        .doc(props.docID)
        .update({
          emailValid: _checked ? 'invalid' : 'new',
        })
        .then(() => {
          setInvalidUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setInvalidUpdating(false);
        });
    },
    [props]
  );

  const onCurrentInvalidChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const _checked = event.target.checked;
      setCurrentInvalid(_checked);
      setInvalidUpdating(true);
      const db = firebase.firestore();
      db.collection('Users')
        .doc(props.docID)
        .update({
          currentValid: _checked ? 'invalid' : 'new',
        })
        .then(() => {
          setInvalidUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setInvalidUpdating(false);
        });
    },
    [props]
  );

  const onHomeInvalidChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const _checked = event.target.checked;
      setHomeInvalid(_checked);
      setInvalidUpdating(true);
      const db = firebase.firestore();
      db.collection('Users')
        .doc(props.docID)
        .update({
          homeValid: _checked ? 'invalid' : 'new',
        })
        .then(() => {
          setInvalidUpdating(false);
        })
        .catch((error) => {
          setInvalidUpdating(false);
          console.error(error);
        });
    },
    [props]
  );
  return (
    <Root>
      {props.isAdmin ? (
        <div>
          <TableHead>
            <tbody>
              <tr>
                <TdLeft>
                  <Name>
                    {props.user.isDead ? '(没)' : undefined}
                    <ruby>
                      {props.user.sei}
                      <rt>{props.user.seiKana}</rt>
                    </ruby>{' '}
                    <ruby>
                      {props.user.mei}
                      <rt>{props.user.meiKana}</rt>
                    </ruby>{' '}
                    ({props.user.enterYear}年度入会)
                  </Name>{' '}
                  <Registered>
                    {props.user.userID === ''
                      ? `仮ID:${
                          props.user.identification === ''
                            ? '未設定'
                            : props.user.identification
                        }`
                      : '登録済'}
                  </Registered>{' '}
                  <IsAdmin>{props.user.isAdmin ? '管理者' : undefined}</IsAdmin>
                </TdLeft>
                <TdRight>
                  {props.showAdditionalInfo === props.docID ? (
                    <button onClick={cancelShowAdditionalInfo}>
                      追加情報を非表示
                    </button>
                  ) : (
                    <button onClick={selectShowAdditionalInfo}>追加情報</button>
                  )}
                  <button onClick={selectShowPayInfo}>特権</button>
                  <button onClick={selectEditable}>編集</button>
                </TdRight>
              </tr>
              <tr>
                <td colSpan={2}>
                  <input
                    type='checkbox'
                    checked={emailInvalid}
                    onChange={onEmailInvalidChange}
                  />
                  Email不着{' '}
                  <input
                    type='checkbox'
                    checked={currentInvalid}
                    onChange={onCurrentInvalidChange}
                  />
                  現住所不着{' '}
                  <input
                    type='checkbox'
                    checked={homeInvalid}
                    onChange={onHomeInvalidChange}
                  />
                  帰省先不着
                  {invalidUpdating ? ' 通信中...' : undefined}
                </td>
              </tr>
            </tbody>
          </TableHead>
          <Table>
            <tbody>
              <tr>
                <TdTag>最終学歴</TdTag>
                <TdVal>{props.user.finalEducation.value}</TdVal>
              </tr>
              <tr>
                <TdTag>勤務先</TdTag>
                <TdVal>{props.user.workplace.value}</TdVal>
              </tr>
              <tr>
                <TdTag>コメント</TdTag>
                <TdVal>{props.user.comment.value}</TdVal>
              </tr>
            </tbody>
          </Table>
          {props.showAdditionalInfo === props.docID ? (
            <div>
              <hr />
              <Table>
                <tbody>
                  {userProperties.map((p) => (
                    <tr key={p.tagKey}>
                      <TdTag>{p.tagName}</TdTag>
                      <TdVal>{p.displayValue}</TdVal>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : undefined}
        </div>
      ) : (
        <div>
          <TableHead>
            <tbody>
              <tr>
                <TdLeft>
                  <Name>
                    {props.user.isDead ? '(没)' : undefined}
                    <ruby>
                      {props.user.sei}
                      <rt>{props.user.seiKana}</rt>
                    </ruby>{' '}
                    <ruby>
                      {props.user.mei}
                      <rt>{props.user.meiKana}</rt>
                    </ruby>{' '}
                    ({props.user.enterYear}年度入会)
                  </Name>
                </TdLeft>
                {props.hasPrivilege ||
                props.user.userID ===
                  (firebase.auth().currentUser?.uid ?? '') ? (
                  <TdRight>
                    {props.showAdditionalInfo === props.docID ? (
                      <button onClick={cancelShowAdditionalInfo}>
                        追加情報を非表示
                      </button>
                    ) : (
                      <button onClick={selectShowAdditionalInfo}>
                        追加情報
                      </button>
                    )}
                  </TdRight>
                ) : undefined}
              </tr>
            </tbody>
          </TableHead>
          <Table>
            <tbody>
              {props.user.finalEducation.value !== '' &&
              props.user.finalEducation.open ? (
                <tr>
                  <TdTag>最終学歴</TdTag>
                  <TdVal>{props.user.finalEducation.value}</TdVal>
                </tr>
              ) : undefined}
              {props.user.workplace.value !== '' &&
              props.user.workplace.open ? (
                <tr>
                  <TdTag>勤務先</TdTag>
                  <TdVal>{props.user.workplace.value}</TdVal>
                </tr>
              ) : undefined}
              {props.user.comment.value !== '' && props.user.comment.open ? (
                <tr>
                  <TdTag>コメント</TdTag>
                  <TdVal>{props.user.comment.value}</TdVal>
                </tr>
              ) : undefined}
            </tbody>
          </Table>
          {props.showAdditionalInfo === props.docID ? (
            <div>
              <hr />
              <Table>
                <tbody>
                  {userProperties.map((p) => (
                    <tr key={p.tagKey}>
                      <TdTag>{p.tagName}</TdTag>
                      <TdVal>{p.displayValue}</TdVal>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : undefined}
        </div>
      )}
    </Root>
  );
});
